/**
 * Virtual Accounts (TMMAPP-0004, design §14.4 row 1; TMMAPP-0159 §E: they behave like My Accounts,
 * reversing TMMAPP-0151 §F 17's selected panel) at `/me/virtual-accounts`.
 *
 * - The v2 header: "Money Tools", the `h1`, the prototype's lead, **Ask about this** (a private
 *   session about the virtual accounts listed) and **New virtual account**.
 * - My Accounts' sections: **Yours** (the person's own ACTIVE virtual accounts, reorderable, each with
 *   Rename and Move earlier / later), ending in the dashed **Add a virtual account** card, then
 *   **Shared with you**. Each section's total sits at its end: the sum of its cards' served
 *   `balanceMinor`, shown only when every card serves one and they share one currency (nothing is
 *   converted). The cards are My Accounts' card grid; a card opens its page (`/v/:id`), whose top
 *   holds what the former panel showed. A stale `?va=` is dropped, with a replace.
 * - The add card goes through `useSubscriberAction()`: a create for a subscriber, the purchase for a
 *   GUEST or NONE tier (never disabled, never a create).
 * - TMMAPP-0005: the owner's ARCHIVED virtual accounts sit behind an "n archived" link — dimmed cards
 *   with Restore (MM-D-62). Never a delete.
 *
 * The list itself comes from the shell's shared fetch (`useVirtualAccountsList`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Link, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { virtualAccountsApi, type VirtualAccountListItem } from '@/api/virtualAccounts';
import { ErrorCodes, isApiError, isNotFound } from '@/api/errors';
import { AddNewCard } from '@/components/AddNewCard';
import { EmptyCard } from '@/components/EmptyCard';
import { ErrorState } from '@/components/ErrorState';
import { ACCOUNT_MIN_CARD_WIDTH, cardGridSx } from '@/components/cardGrid';
import { MoneyParts } from '@/components/MoneyText';
import { SortableCardGrid } from '@/components/SortableCardGrid';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import { DeletionPendingAlert } from '@/session/DeletionPendingAlert';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { booleanParam, useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { ArchivedVirtualAccountCard } from './ArchivedVirtualAccountCard';
import { VirtualAccountCard } from './VirtualAccountCard';
import { RenameVirtualAccountDialog } from './RenameVirtualAccountDialog';
import { useVirtualAccountsList } from './virtualAccountsListContext';

const SKELETON_KEYS = ['a', 'b', 'c'] as const;
const URL_SCHEMA = { archived: booleanParam() };

function CardGrid({ children }: { children: ReactNode }) {
  return <Box sx={cardGridSx()}>{children}</Box>;
}

/**
 * A section's total: the sum of its cards' served balances when every card serves one in one
 * currency, else null (no total shows, as on My Accounts' sections where none is served).
 */
function sectionTotal(
  rows: readonly VirtualAccountListItem[],
): { minor: number; currency: string } | null {
  const first = rows[0];
  if (first === undefined) return null;
  let minor = 0;
  for (const row of rows) {
    if (row.balanceMinor === null || row.currency !== first.currency) return null;
    minor += row.balanceMinor;
  }
  return { minor, currency: first.currency };
}

/** The dashed add card (My Accounts' `AddNewCard`): a create, or the purchase for anyone else. */
function AddCard({ add }: { add: ReturnType<typeof useSubscriberAction> }) {
  const { t } = useTranslation();
  return (
    <>
      <AddNewCard
        label={t('virtualAccounts.addNew')}
        hint={t('virtualAccounts.addNewHint')}
        minHeight={200}
        describedBy={add.descriptionId}
        onClick={add.run}
      />
      <SubscriberActionHint id={add.descriptionId} description={add.description} />
    </>
  );
}

/**
 * An empty list (pattern 9, TMMAPP-0117 B12): the empty card, then the dashed add card. The add goes
 * through `useSubscriberAction()`: a create for a subscriber, the purchase for anyone else.
 */
function EmptyWithAdd({
  empty,
  add,
}: {
  empty: string;
  add: ReturnType<typeof useSubscriberAction>;
}) {
  return (
    <CardGrid>
      {/* The empty card fills the grid row beside the add card. */}
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', '& > *': { flex: 1 } }}>
        <EmptyCard>{empty}</EmptyCard>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <AddCard add={add} />
      </Box>
    </CardGrid>
  );
}

export function VirtualAccountsPage() {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const refetchMe = useMeRefetch();
  const notify = useNotify();
  const { status, items, error, reload, openCreate } = useVirtualAccountsList();
  const [renameTarget, setRenameTarget] = useState<VirtualAccountListItem | null>(null);
  const [{ archived: showArchived }, setUrl] = useUrlState(URL_SCHEMA);
  // A card opens its page (§E 15): a stale `?va=` selection is dropped, with a replace.
  const [search, setSearch] = useSearchParams();
  useEffect(() => {
    if (!search.has('va')) return;
    const next = new URLSearchParams(search);
    next.delete('va');
    setSearch(next, { replace: true });
  }, [search, setSearch]);
  const [restoring, setRestoring] = useState(false);
  const [overlay, setOverlay] = useState<{ key: string; ids: number[] } | null>(null);
  const reorderTicket = useRef(0);
  const title = usePageTitle({ title: t('toolsNav.virtualAccounts') });

  const subscriber = me.tier === 'SUBSCRIBER';
  const createVirtualAccount = useSubscriberAction(() => openCreate());
  const mineBase = items.filter((d) => d.mine && d.status === 'ACTIVE');
  const archived = items.filter((d) => d.mine && d.status === 'ARCHIVED');
  const shared = items.filter((d) => !d.mine);
  const mineKey = mineBase.map((virtualAccount) => virtualAccount.id).join(',');
  const overlaid =
    overlay !== null && overlay.key === mineKey
      ? overlay.ids.flatMap((id) => {
          const row = mineBase.find((virtualAccount) => virtualAccount.id === id);
          return row ? [row] : [];
        })
      : null;
  const mine = overlaid !== null && overlaid.length === mineBase.length ? overlaid : mineBase;
  const cards = [...mine, ...shared];

  async function reorderMine(ids: Array<string | number>) {
    const ticket = ++reorderTicket.current;
    const nextIds = ids.map(Number);
    setOverlay({ key: mineKey, ids: nextIds });
    try {
      await virtualAccountsApi.reorder(nextIds);
      if (ticket !== reorderTicket.current) return;
      await Promise.all([reload(), refetchMe()]);
    } catch (error) {
      if (ticket !== reorderTicket.current) return;
      setOverlay(null);
      if (isApiError(error) && error.code === ErrorCodes.REORDER_SET_MISMATCH) {
        notify(t('rules.reorderMismatch'), { severity: 'error' });
        await reload();
      } else if (isApiError(error) && error.code === ErrorCodes.USER_FROZEN) {
        notify(t('errors.userFrozen'), { severity: 'error' });
      } else if (isApiError(error) && isNotFound(error)) {
        notify(t('errors.notFound'), { severity: 'error' });
        await reload();
      } else if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        notify(t('errors.subscribe'), { severity: 'error' });
      } else {
        notify(t('virtualAccounts.errorGeneric'), { severity: 'error' });
      }
      throw error;
    }
  }

  async function restore(virtualAccount: VirtualAccountListItem) {
    if (restoring) return;
    setRestoring(true);
    try {
      await virtualAccountsApi.restore(virtualAccount.id);
      await Promise.all([reload(), refetchMe()]);
    } catch (error) {
      notify(
        isApiError(error) && error.code === 'VIRTUAL_ACCOUNT_NAME_TAKEN'
          ? t('virtualAccounts.errorRestoreNameTaken')
          : t('virtualAccounts.errorGeneric'),
        { severity: 'error' },
      );
    } finally {
      setRestoring(false);
    }
  }

  /** A section as My Accounts draws one: its `h2`, its total at the end, then its grid. */
  function section(
    key: 'mine' | 'shared',
    rows: readonly VirtualAccountListItem[],
    body: ReactNode,
  ) {
    const headingId = `virtual-accounts-section-${key}`;
    const total = sectionTotal(rows);
    return (
      <Box
        component="section"
        aria-labelledby={headingId}
        data-virtual-accounts-section={key}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h3" component="h2" id={headingId} sx={{ flex: 1, fontSize: 24 }}>
            {t(key === 'mine' ? 'virtualAccounts.sectionMine' : 'virtualAccounts.sectionShared')}
          </Typography>
          {total !== null && (
            <Typography
              component="span"
              data-section-total=""
              sx={{ fontSize: 14, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
            >
              <MoneyParts minor={total.minor} currency={total.currency} whole />
            </Typography>
          )}
        </Box>
        {body}
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title={title}
        subtitle={t('virtualAccounts.lead')}
        actions={
          <>
            <AskAboutThis
              name={t('askAbout.nameAbout', { name: title ?? '' })}
              scope={
                status !== 'ready'
                  ? null
                  : cards.map((card) => ({ kind: 'VIRTUAL_ACCOUNT' as const, id: card.id }))
              }
            />
            <Button
              variant="contained"
              aria-describedby={createVirtualAccount.descriptionId}
              onClick={createVirtualAccount.run}
            >
              {t('virtualAccounts.new')}
            </Button>
            <SubscriberActionHint
              id={createVirtualAccount.descriptionId}
              description={createVirtualAccount.description}
            />
          </>
        }
      />

      {me.deletionRequested !== null && (
        <DeletionPendingAlert until={me.deletionRequested.coolingOffUntil} settingsLink />
      )}

      {status === 'loading' && (
        <Box sx={cardGridSx(undefined, ACCOUNT_MIN_CARD_WIDTH)}>
          {SKELETON_KEYS.map((key) => (
            <Box key={key} sx={{ minWidth: 0 }}>
              <Skeleton variant="rounded" height={160} />
            </Box>
          ))}
        </Box>
      )}

      {status === 'error' && (
        <ErrorState
          error={error}
          what={t('errors.what.virtualAccounts')}
          onRetry={() => void reload()}
        />
      )}

      {status === 'ready' && cards.length === 0 && (
        // Nothing owned and nothing shared: the empty card, then the add card (a create for a
        // subscriber, the purchase for anyone else; pattern 9, TMMAPP-0117 B12).
        <EmptyWithAdd
          empty={t(subscriber ? 'virtualAccounts.emptyMine' : 'virtualAccounts.emptyShared')}
          add={createVirtualAccount}
        />
      )}

      {status === 'ready' &&
        cards.length > 0 &&
        section(
          'mine',
          mine,
          <SortableCardGrid
            items={mine}
            getId={(virtualAccount) => virtualAccount.id}
            getName={(virtualAccount) => virtualAccount.name}
            canReorder={mine.length > 0}
            isSortable={(virtualAccount) => virtualAccount.mine}
            onReorder={reorderMine}
            minCardWidth={ACCOUNT_MIN_CARD_WIDTH}
            trailing={<AddCard add={createVirtualAccount} />}
            renderCard={(virtualAccount, reorder) => (
              <VirtualAccountCard
                virtualAccount={virtualAccount}
                {...(reorder
                  ? { actions: { onRename: () => setRenameTarget(virtualAccount), reorder } }
                  : {})}
              />
            )}
          />,
        )}

      {status === 'ready' &&
        shared.length > 0 &&
        section(
          'shared',
          shared,
          <SortableCardGrid
            items={shared}
            getId={(virtualAccount) => virtualAccount.id}
            getName={(virtualAccount) => virtualAccount.name}
            canReorder={false}
            onReorder={() => Promise.resolve()}
            minCardWidth={ACCOUNT_MIN_CARD_WIDTH}
            renderCard={(virtualAccount) => <VirtualAccountCard virtualAccount={virtualAccount} />}
          />,
        )}

      {status === 'ready' && archived.length > 0 && (
        <Box>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setUrl({ archived: !showArchived })}
            aria-expanded={showArchived}
          >
            {t('virtualAccounts.archivedCount', { count: archived.length })}
          </Link>
        </Box>
      )}

      {status === 'ready' && showArchived && archived.length > 0 && (
        <Box component="section">
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            {t('virtualAccounts.archived')}
          </Typography>
          <CardGrid>
            {archived.map((virtualAccount) => (
              <Box key={virtualAccount.id} sx={{ minWidth: 0 }}>
                <ArchivedVirtualAccountCard
                  virtualAccount={virtualAccount}
                  onRestore={() => void restore(virtualAccount)}
                  disabled={restoring}
                />
              </Box>
            ))}
          </CardGrid>
        </Box>
      )}

      {renameTarget && (
        <RenameVirtualAccountDialog
          key={renameTarget.id}
          virtualAccount={renameTarget}
          onClose={() => setRenameTarget(null)}
          onRenamed={async () => {
            await Promise.all([reload(), refetchMe()]);
          }}
        />
      )}
    </Stack>
  );
}
